import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box, MenuItem, Typography } from "@mui/material";
import { ChoiceChipGroup, FormSelect } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useClinicModule } from "../../../hooks/useClinicModule";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { formatMoney } from "../../../utils/money";
import { useListBillingServicesMutation, useListPatientPackagesMutation } from "../../../store/billingApi";
import { dateTr } from "./billingParts";

/**
 * Randevu formunda hizmet seçimi (+ hastanın o hizmete ait aktif paketi varsa
 * "Paketten düş"). Billing modülü kapalıysa ya da `services:read` yoksa hiç çizilmez.
 *
 * @param {string} token
 * @param {string} patientId
 * @param {string} serviceId
 * @param {string} patientPackageId
 * @param {({serviceId, patientPackageId, service}) => void} onChange
 *   `service` seçilen hizmetin tamamı (süre için) ya da null.
 * @param {string} [clinicId] - admin bağlamı
 * @param {boolean} [disabled]
 */
export default function AppointmentServiceFields({
  token,
  patientId,
  serviceId,
  patientPackageId,
  onChange,
  clinicId,
  disabled = false,
}) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const billingActive = useClinicModule("billing");
  const canServices = useHasPermission("services:read");
  const canPackages = useHasPermission("packages:read");
  const enabled = billingActive && canServices;

  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [listServices] = useListBillingServicesMutation();
  const [listPackages] = useListPatientPackagesMutation();
  const scopeKey = clinicId || "";

  useEffect(() => {
    if (!enabled || !token) return;
    let alive = true;
    listServices({ token, ...(scopeKey ? { clinicId: scopeKey } : {}) })
      .unwrap()
      .then((r) => alive && setServices(r?.data?.services || []))
      .catch(() => alive && setServices([]));
    return () => {
      alive = false;
    };
  }, [enabled, token, scopeKey, listServices]);

  useEffect(() => {
    if (!enabled || !canPackages || !token || !patientId || !serviceId) {
      setPackages([]);
      return undefined;
    }
    let alive = true;
    listPackages({ token, patientId, serviceId, usableOnly: true, ...(scopeKey ? { clinicId: scopeKey } : {}) })
      .unwrap()
      .then((r) => alive && setPackages(r?.data?.packages || []))
      .catch(() => alive && setPackages([]));
    return () => {
      alive = false;
    };
  }, [enabled, canPackages, token, patientId, serviceId, scopeKey, listPackages]);

  // Seçili paket artık listede yoksa (hasta/hizmet değişti) bağı kopar
  useEffect(() => {
    if (patientPackageId && serviceId && !packages.some((p) => p._id === patientPackageId)) {
      onChange({ serviceId, patientPackageId: "", service: services.find((s) => s._id === serviceId) || null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);

  if (!enabled) return null;

  const selected = services.find((s) => s._id === serviceId) || null;
  const pkgOptions = [
    { value: "", label: selected ? `Ücretli · ${formatMoney(selected.price)}` : "Ücretli" },
    ...packages.map((p) => ({
      value: p._id,
      label: `Paketten düş · ${p.template?.name} (${p.remaining} hak, ${dateTr(p.expiresAt)})`,
    })),
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormSelect
        palette={palette}
        variant="standard"
        label="Hizmet (opsiyonel)"
        value={serviceId || ""}
        disabled={disabled}
        onChange={(v) =>
          onChange({ serviceId: v, patientPackageId: "", service: services.find((s) => s._id === v) || null })
        }
        placeholder={null}
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="">Hizmet seçilmedi</MenuItem>
        {services.map((s) => (
          <MenuItem key={s._id} value={s._id}>
            {s.name} · {s.durationMin} dk · {formatMoney(s.price)}
          </MenuItem>
        ))}
      </FormSelect>
      {selected && packages.length > 0 ? (
        <Box>
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500, mb: 0.75 }}>
            {terms.patientGen} bu hizmete ait aktif paketi var
          </Typography>
          <ChoiceChipGroup
            ariaLabel="Ödeme şekli"
            value={patientPackageId || ""}
            onChange={(v) => onChange({ serviceId, patientPackageId: v || "", service: selected })}
            options={pkgOptions}
            disabled={disabled}
            wrapLabels
          />
          {patientPackageId ? (
            <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mt: 0.75 }}>
              Randevu tamamlanınca paketten bir seans düşer; iptal edilirse geri gelir.
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
